import { ChevronLeft } from 'lucide-react';
import React, { useEffect } from 'react';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';

/* ======================================================
   THEME (same as OptiiiPage)
====================================================== */

const theme = {
  bg: '#07070A',
  bgAlt: 'rgba(255,255,255,0.03)',
  card: 'rgba(255,255,255,0.06)',
  text: '#F5F5F7',
  subText: 'rgba(245,245,247,0.72)',
  accent: '#7C3AED',
  border: 'rgba(255,255,255,0.12)',
};

const GlobalStyle = createGlobalStyle`
  :root { color-scheme: dark; }
  body {
    margin: 0;
    background: ${theme.bg};
    color: ${theme.text};
    font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
  }
`;

/* ======================================================
   PAGE
====================================================== */

const TermsPage = () => {
  useEffect(() => window.scrollTo(0, 0), []);
  const path = window.location.pathname;

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />

      <Page>
        {/* ===== LEGAL NAV ===== */}
        <Nav>
          <NavInner>
            <NavLeft onClick={() => (window.location.href = '/')}>
              <ChevronLeft /> Back to Optiii
            </NavLeft>

            <NavLinks>
              <NavLink href="/privacy" $active={path === '/privacy'}>
                Privacy
              </NavLink>
              <NavLink href="/terms" $active={path === '/terms'}>
                Terms
              </NavLink>
            </NavLinks>
          </NavInner>
        </Nav>

        {/* ===== HERO ===== */}
        <Hero>
          <HeroInner>
            <Eyebrow>Optiii</Eyebrow>
            <HeroTitle>Terms of service</HeroTitle>
            <HeroSub>
              Legal terms governing your access to and use of the Optiii platform and services.
            </HeroSub>
          </HeroInner>
        </Hero>

        {/* ===== CONTENT ===== */}
        <Section>
          <Inner>
            <Card>
              <SectionTitle>1. User acknowledgment and acceptance of terms</SectionTitle>

              <Paragraph>
                Optiii (“Optiii”, “us”, or “we”) provides the optiii.com website and related
                services (collectively, the “site”) subject to your compliance with these
                Terms of Service.
              </Paragraph>

              <Paragraph>
                By using this site, you agree to be bound by these Terms of Service. If you do
                not agree, you must discontinue use of the site immediately.
              </Paragraph>

              <Paragraph>
                These Terms are effective as of <strong>1 November 2024</strong>. We reserve
                the right to modify these Terms at any time. Continued use of the site
                constitutes acceptance of any updated Terms.
              </Paragraph>

              <Divider />

              <SectionTitle>2. Description of services</SectionTitle>

              <Paragraph>
                Optiii provides services including, but not limited to, document ingestion,
                policy and rulebook creation, analytical tooling, automation workflows, and
                related advisory and software services.
              </Paragraph>

              <Paragraph>
                We reserve the right to modify or discontinue any aspect of the site or
                services at any time without notice.
              </Paragraph>

              <Divider />

              <SectionTitle>3. Registration data and privacy</SectionTitle>

              <Paragraph>
                Certain services require account registration. You agree that all information
                provided is accurate and kept up to date.
              </Paragraph>

              <Paragraph>
                Use of personal information is governed by our{' '}
                <EmailLink href="/privacy">Privacy Policy</EmailLink>.
              </Paragraph>

              <Divider />

              <SectionTitle>4. Conduct on site</SectionTitle>

              <Paragraph>
                You agree not to use the site to upload, distribute, or facilitate content that:
              </Paragraph>

              <List>
                <li>Is unlawful, abusive, defamatory, fraudulent, or invasive of privacy</li>
                <li>Infringes intellectual property or proprietary rights</li>
                <li>Contains malware, viruses, or malicious code</li>
                <li>Constitutes spam, unsolicited advertising, or impersonation</li>
                <li>Interferes with system security or other users</li>
              </List>

              <Paragraph>
                Optiii reserves the right to remove content and terminate accounts at its sole
                discretion.
              </Paragraph>

              <Divider />

              <SectionTitle>5. Subscriptions and billing</SectionTitle>

              <Paragraph>
                Subscriptions are billed in advance. Usage-based charges are typically invoiced
                in arrears.
              </Paragraph>

              <Paragraph>
                Accounts with outstanding balances exceeding 10 calendar days may have access
                restricted until payment is received.
              </Paragraph>

              <Divider />

              <SectionTitle>6. Third-party sites</SectionTitle>

              <Paragraph>
                The site may link to third-party websites. Optiii is not responsible for the
                content, legality, or accuracy of third-party materials.
              </Paragraph>

              <Divider />

              <SectionTitle>7. Intellectual property</SectionTitle>

              <Paragraph>
                All content on this site is the property of Optiii or its licensors and is
                protected by intellectual property laws.
              </Paragraph>

              <Paragraph>
                You may not reproduce, distribute, or commercially exploit any content without
                prior written consent.
              </Paragraph>

              <Divider />

              <SectionTitle>8. Accuracy of information</SectionTitle>

              <Paragraph>
                Information provided on this site is for general informational purposes only
                and should not be relied upon for commercial decisions without independent
                verification.
              </Paragraph>

              <Divider />

              <SectionTitle>9. Disclaimer of warranties</SectionTitle>

              <Paragraph>
                All services are provided “as is” and “as available” without warranties of any
                kind, express or implied.
              </Paragraph>

              <Divider />

              <SectionTitle>10. Limitation of liability</SectionTitle>

              <Paragraph>
                To the maximum extent permitted by law, Optiii shall not be liable for indirect,
                incidental, consequential, or punitive damages arising from use of the site or
                services.
              </Paragraph>

              <Divider />

              <SectionTitle>11. Indemnification</SectionTitle>

              <Paragraph>
                You agree to indemnify and hold harmless Optiii and its affiliates from any
                claims arising out of your use of the site or breach of these Terms.
              </Paragraph>

              <Divider />

              <SectionTitle>12. Governing law</SectionTitle>

              <Paragraph>
                These Terms are governed by the laws of New South Wales, Australia. You submit
                to the exclusive jurisdiction of the courts of that jurisdiction.
              </Paragraph>

              <Divider />

              <SectionTitle>13. Contact</SectionTitle>

              <Paragraph>
                For questions regarding these Terms, contact{' '}
                <EmailLink href="mailto:team@optiii.com">
                  team@optiii.com
                </EmailLink>
              </Paragraph>
            </Card>
          </Inner>
        </Section>
      </Page>
    </ThemeProvider>
  );
};

export default TermsPage;

/* ======================================================
   STYLES
====================================================== */

const Page = styled.div``;

const Hero = styled.section`
  padding: 160px 24px 0px;
`;

const HeroInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 13px;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.subText};
  margin-bottom: 14px;
`;

const HeroTitle = styled.h1`
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 600;
  margin: 0 0 12px;
`;

const HeroSub = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.subText};
  max-width: 720px;
  line-height: 1.7;
`;

const Section = styled.section`
  padding: 80px 24px 140px;
`;

const Inner = styled.div`
  max-width: 980px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: ${({ theme }) => theme.card};
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 24px;
  padding: 36px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 800;
  margin: 32px 0 14px;
`;

const Paragraph = styled.p`
  font-size: 15px;
  line-height: 1.75;
  color: ${({ theme }) => theme.subText};
  margin: 0 0 14px;
`;

const List = styled.ul`
  padding-left: 20px;
  margin: 12px 0 24px;
  color: ${({ theme }) => theme.subText};

  li {
    margin-bottom: 10px;
    line-height: 1.6;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.border};
  margin: 36px 0;
`;

const EmailLink = styled.a`
  color: #fff;
  text-decoration: none;
  border-bottom: 1px solid rgba(255,255,255,0.35);

  &:hover {
    border-color: #fff;
  }
`;

/* --- Legal Nav --- */

const Nav = styled.div`
  position: fixed;
  top: 16px;
  left: 0;
  right: 0;
  z-index: 90;
  padding: 0 18px;
`;

const NavInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(14px);
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 999px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NavLeft = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.subText};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    background: rgba(255,255,255,0.06);
    color: ${({ theme }) => theme.text};
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 8px;
`;

const NavLink = styled.a`
  font-size: 14px;
  padding: 8px 14px;
  border-radius: 999px;
  text-decoration: none;
  cursor: pointer;
  color: ${({ theme, $active }) =>
    $active ? '#fff' : theme.subText};
  background: ${({ $active }) =>
    $active ? 'rgba(255,255,255,0.12)' : 'transparent'};
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.border : 'transparent'};

  &:hover {
    background: rgba(255,255,255,0.08);
    color: #fff;
  }
`;
